<template>
  <div class="project-forms">
    <!-- 工具栏 -->
    <div class="toolbar">
      <div class="toolbar-left">
        <el-button type="primary" icon="el-icon-plus" @click="createFormVisible = true">新建问卷</el-button>
        <el-button class="ghost" @click="onCreateFolder">新建文件夹</el-button>
      </div>
      <div class="toolbar-right">
        <el-input 
          v-model="queryParams.name" 
          class="search" 
          placeholder="请输入问卷名称搜索" 
          prefix-icon="el-icon-search" 
          @keyup.enter.native="queryFormList" 
        />
        <el-select v-model="filter.status" class="filter" placeholder="发布状态" clearable @change="onFilterChange">
          <el-option v-for="(o, i) in statusOptions" :key="'status-'+i" :label="o.label" :value="o.value" />
        </el-select>
        <el-select v-model="filter.type" class="filter" placeholder="选择类型" @change="onFilterChange">
          <el-option v-for="(o, i) in typeOptions" :key="'type-'+i" :label="o.label" :value="o.value" />
        </el-select>
        <el-button circle icon="el-icon-menu" class="icon-btn" @click="onSwitchView" :title="dataShowType === 'card' ? '切换表格视图' : '切换卡片视图'" />
      </div>
    </div>

    <!-- 问卷列表内容区 -->
    <div class="content">
      <div class="section-title">
        项目问卷
        <span class="form-count" v-if="total > 0">（共 {{ total }} 份）</span>
      </div>
      
      <!-- 卡片视图 -->
      <form-card-list 
        v-if="dataShowType === 'card'" 
        v-loading="loading" 
        :project-list.sync="formListView" 
        @refresh="queryFormList" 
      />
      
      <!-- 表格视图 -->
      <my-form-table 
        v-if="dataShowType === 'table'" 
        v-loading="loading" 
        :project-list.sync="formListView" 
        @refresh="queryFormList" 
      />
      
      <!-- 空状态 -->
      <div v-if="!loading && formListView.length === 0" class="empty-state">
        <i class="el-icon-document" />
        <p>暂无问卷</p>
        <el-button type="primary" @click="createFormVisible = true">立即创建</el-button>
      </div>
      
      <!-- 分页 -->
      <div class="page-view">
        <el-pagination 
          v-if="total > queryParams.size" 
          :current-page.sync="queryParams.current" 
          :page-size.sync="queryParams.size" 
          :total="total" 
          background 
          layout="total, prev, pager, next" 
          @current-change="queryFormList" 
        />
      </div>
    </div>

    <!-- 新建问卷对话框 -->
    <el-dialog 
      class="create-form-dialog" 
      :visible.sync="createFormVisible" 
      :modal="true" 
      :append-to-body="true" 
      title="创建问卷" 
      width="60%"
    >
      <create-form ref="createForm" @select="handleTypeSelect" @operation="handleImportOp" />
    </el-dialog>
  </div>
</template>

<script>
import { createFormRequest, pageFormRequest } from 'index/api/project/form'
import FormCardList from '../card'
import CreateForm from '../CreateForm'
import MyFormTable from '../table'

export default {
  name: 'ProjectForms',
  
  components: { FormCardList, CreateForm, MyFormTable },
  
  data() {
    return {
      createFormVisible: false,
      dataShowType: 'card',
      total: 0,
      loading: true,
      filter: { status: null, type: 'all' },
      statusOptions: [
        { label: '全部状态', value: null },
        { label: '已发布', value: 2 },
        { label: '未发布', value: 1 }
      ],
      typeOptions: [
        { label: '全部', value: 'all' },
        { label: '问卷调查', value: 1 },
        { label: '收集表单', value: 2 },
        { label: '考试测评', value: 3 },
        { label: '评估问卷', value: 4 },
        { label: '360评估', value: 5 }
      ],
      queryParams: { 
        page: 1, 
        size: 12, 
        name: '', 
        status: null, 
        folderId: 0, 
        current: 1, 
        projectId: null 
      },
      formList: [],
      formListView: []
    }
  },

  computed: {
    // 从路由参数获取项目ID
    projectId() {
      return this.$route.params.projectId ? parseInt(this.$route.params.projectId) : null
    }
  },

  watch: {
    // 监听项目ID变化，重新加载数据
    projectId: {
      immediate: true,
      handler(newId) {
        if (newId) {
          this.queryParams.projectId = newId
          this.queryFormList()
        }
      }
    }
  },

  methods: {
    // 查询问卷列表
    queryFormList() {
      this.loading = true
      this.queryParams.projectId = this.projectId
      
      pageFormRequest(this.queryParams).then((res) => {
        const { records, total, size } = res.data.data
        this.formList = records || []
        this.applyLocalFilter()
        this.total = total
        this.queryParams.size = size
        this.loading = false
      }).catch(() => {
        this.loading = false
      })
    },
    
    // 应用本地筛选
    applyLocalFilter() {
      let list = this.formList.slice()
      if (this.filter.status !== null) {
        list = list.filter((p) => String(p.status) === String(this.filter.status))
      }
      if (this.filter.type !== 'all') {
        list = list.filter((p) => String(p.type) === String(this.filter.type))
      }
      if (this.queryParams.name) {
        const kw = this.queryParams.name.toLowerCase()
        list = list.filter((p) => (p.textName || p.name || '').toLowerCase().includes(kw))
      }
      this.formListView = list
    },
    
    // 筛选变化
    onFilterChange() {
      this.applyLocalFilter()
    },
    
    // 切换视图
    onSwitchView() {
      this.dataShowType = this.dataShowType === 'card' ? 'table' : 'card'
    },
    
    // 创建文件夹（占位）
    onCreateFolder() {
      this.$message.success('已创建文件夹')
    },
    
    // 处理问卷类型选择
    handleTypeSelect(payload) {
      const type = payload && payload.type
      const title = (payload && payload.title) ? payload.title : '未命名问卷'
      const desc = (payload && payload.desc) ? payload.desc : ''
      
      const formType = type || 1
      const body = { 
        title, 
        desc, 
        type: formType,
        formType: formType,
        form_type: formType,
        folderId: this.queryParams.folderId || 0,
        projectId: this.projectId  // 关键：绑定当前项目ID
      }
      
      this.createFormVisible = false
      createFormRequest(body)
        .then((res) => { 
          this.$message.success('问卷创建成功')
          console.log('[Forms] 创建问卷返回:', res)
          // 获取新创建问卷的 formKey，跳转到编辑页面（兼容多种字段名）
          const data = res?.data?.data || res?.data || res || {}
          const formKey = data.formKey || data.form_key || data.key
          console.log('[Forms] 解析到的 formKey:', formKey)
          if (formKey) {
            // 跳转到问卷编辑页面（正确的路由格式）
            window.location.href = `/survey/form?key=${formKey}`
          } else {
            // 如果没有返回 formKey，则刷新列表
            this.queryFormList()
          }
        })
        .catch((err) => { 
          this.$message.error('创建失败：' + (err.message || '未知错误'))
        })
    },
    
    // 导入操作（占位）
    handleImportOp(op) {
      this.$message.info(op)
    }
  }
}
</script>

<style lang="scss" scoped>
.project-forms {
  padding: 0;
}

.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fff;
  padding: 12px 0;
  border-radius: 8px;
  margin-bottom: 16px;
}

.toolbar-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.toolbar-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.ghost {
  border: 1px solid #e5e7eb;
}

.search {
  width: 280px;
}

.filter {
  width: 130px;
}

.icon-btn {
  border: none;
}

.content {
  margin-top: 0;
}

.section-title {
  font-size: 16px;
  color: #303133;
  margin-bottom: 16px;
  font-weight: 500;
  
  .form-count {
    font-size: 13px;
    color: #909399;
    font-weight: normal;
  }
}

.empty-state {
  text-align: center;
  padding: 80px 20px;
  color: #909399;
  
  i {
    font-size: 64px;
    margin-bottom: 16px;
    color: #dcdfe6;
  }
  
  p {
    margin-bottom: 20px;
  }
}

.page-view {
  margin-top: 20px;
  text-align: center;
}

.create-form-dialog {
  border-radius: 10px !important;
}

::v-deep .create-form-dialog .el-dialog__body {
  padding: 0 !important;
  color: inherit;
  font-size: 14px;
  word-break: break-all;
}
</style>
